import { Button, useFetch } from "@4uhub/lib4uhub";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import WhereToVoteRoundedIcon from "@mui/icons-material/WhereToVoteRounded";
import {
  Box,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDiffDates } from "../../../hooks/useDiffDates";
import { useDistance } from "../../../hooks/useDistance";
import { checkInTriage } from "../../../services/triageEmergencyDepartment.service";
import { notificationSliceActions } from "../../../store/slices/notification";
import { useAppDispatch } from "../../../store/store";
import { ICheckInButtonProps } from "./models";

const translationPath = "components.checkInButton.";

export const CheckInButtonTriage = memo(
  ({
    checkInRule,
    created,
    id,
    patientEncounterId,
    individualTasyInsuranceFavorite,
  }: ICheckInButtonProps) => {
    const { t } = useTranslation();

    const theme = useTheme();

    const matches = useMediaQuery(theme.breakpoints.down("md"));

    const { distanceBetweenCoordinates } = useDistance();
    const dispatch = useAppDispatch();

    const { calculateDiffDates } = useDiffDates();

    const [checked, setChecked] = useState<boolean>(false);

    const [position, setPosition] = useState<GeolocationPosition | null>(null);

    const [positionError, setPositionError] =
      useState<GeolocationPositionError | null>(null);

    const [isNear, setIsNear] = useState(false);

    const insuranceCode =
      individualTasyInsuranceFavorite.insuranceIntegrationId;

    const insurances = useMemo(
      () =>
        checkInRule && checkInRule.insuranceAllowCheckin
          ? checkInRule.insuranceAllowCheckin.split(",").map((i) => i.trim())
          : [],
      [checkInRule]
    );

    const insuranceAllowed = useMemo(
      () =>
        !checkInRule.insuranceAllowCheckin
          ? true
          : insurances.some((i) => i === insuranceCode),
      [checkInRule, insuranceCode, insurances]
    );

    const { sendRequest, loading } = useFetch(checkInTriage);

    const successCallback = useCallback((position: GeolocationPosition) => {
      setPosition(position);
    }, []);

    const errorCallback = useCallback((error: GeolocationPositionError) => {
      setPositionError(error);
    }, []);

    const fetchLocation = useCallback(() => {
      navigator.geolocation.getCurrentPosition(successCallback, errorCallback);
    }, [successCallback, errorCallback]);

    const checkInHandler = useCallback(
      async (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation();
        if (!id) return;
        const { data, success } = await sendRequest({
          triageEmergencyDepartmentId: id,
        });
        if (data && success) {
          setChecked(true);
          dispatch(
            notificationSliceActions.showNotification({
              message: `${t(translationPath + "checkin_successfully")}`,
              type: "success",
            })
          );
        }
      },
      [sendRequest, dispatch, t, id]
    );

    useEffect(() => {
      fetchLocation();
    }, [fetchLocation]);

    const distance = useMemo(
      () =>
        position && checkInRule
          ? distanceBetweenCoordinates(
              Number(checkInRule.latitude),
              Number(checkInRule.longitude),
              position.coords.latitude,
              position.coords.longitude,
              "meters"
            )
          : null,
      [checkInRule, position, distanceBetweenCoordinates]
    );

    const minutesToSchedule =
      calculateDiffDates(created, "minutes") ?? Infinity;

    const isInTime = !!(
      checkInRule &&
      minutesToSchedule >= 0 &&
      minutesToSchedule <= 120 &&
      minutesToSchedule <= checkInRule.time
    );

    const isLate = minutesToSchedule > 120;

    useEffect(() => {
      if (distance === null) return;

      if (checkInRule && distance <= checkInRule.metersApproximation) {
        setIsNear(true);
        return;
      }
      setIsNear(false);
    }, [distance, checkInRule]);

    let content = <Typography variant="subtitle2"> - </Typography>;

    if (!patientEncounterId) {
      content = (
        <Button
          disabled={
            !!positionError ||
            !position ||
            !isNear ||
            !isInTime ||
            isLate ||
            !insuranceAllowed
          }
          loading={loading}
          startIcon={<WhereToVoteRoundedIcon />}
          onClick={checkInHandler}
          sx={(t) => ({
            backgroundColor:
              t.palette.mode === "light" ? "white" : t.palette.grey[800],
            color: t.palette.primary.main,
            "&:hover": {
              backgroundColor: t.palette.primary.dark,
              color: "white",
            },
          })}
        >
          {t(`${translationPath}check_in`)}
        </Button>
      );
    }
    if (patientEncounterId || checked) {
      content = (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VerifiedOutlinedIcon color="success" sx={{ fontSize: 30 }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={(t) => ({
                color: t.palette.grey[t.palette.mode === "light" ? 600 : 400],
              })}
            >
              {t(`${translationPath}checked`)}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Tooltip
        title={
          !!positionError || !position
            ? t(`${translationPath}location`)
            : isLate
            ? t(`${translationPath}is_late`)
            : !isInTime && checkInRule
            ? t(`${translationPath}is_in_time_triage`, {
                time: checkInRule.time,
              })
            : !isNear && checkInRule
            ? t(`${translationPath}is_near`, {
                meters: checkInRule.metersApproximation,
              })
            : !insuranceAllowed
            ? t(`${translationPath}insurance_not_allowed`)
            : ""
        }
        disableHoverListener={
          patientEncounterId ||
          (position && isNear && isInTime && insuranceAllowed && !isLate)
            ? true
            : false
        }
        disableFocusListener={
          patientEncounterId ||
          (position && isNear && isInTime && insuranceAllowed && !isLate)
            ? true
            : false
        }
      >
        <Box
          sx={{
            width: matches ? "100%" : "fit-content",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {content}
        </Box>
      </Tooltip>
    );
  }
);
